import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, Gift } from "lucide-react";

const MAX_ATTEMPTS = 12;
const POLL_MS = 2000;

export default function GiftCardSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling" });
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Session invalide." });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/gift-cards/payment-status/${sessionId}`);
        // Response no longer includes the gift code (delivered by email only).
        // Consider the flow complete as soon as Stripe reports paid.
        if (data.payment_status === "paid") {
          setInfo(data);
          setState({ status: "paid" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", message: "Session expirée." });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", message: "Le traitement prend plus longtemps que prévu. La confirmation arrivera par email." });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (err) {
        // Transient errors (Cloudflare 5xx, network blips) — keep polling silently
        const status = err?.response?.status;
        const isClientError = status >= 400 && status < 500;
        if (isClientError || attempts >= MAX_ATTEMPTS) {
          setState({
            status: attempts >= MAX_ATTEMPTS ? "timeout" : "error",
            message: attempts >= MAX_ATTEMPTS
              ? "Le traitement prend plus longtemps que prévu. La confirmation arrivera par email."
              : (formatApiError(err.response?.data?.detail) || "Erreur de vérification."),
          });
          return;
        }
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="gc-success-page">
      {state.status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-primary mx-auto mb-6 animate-spin" />
          <h1 className="font-display text-4xl mb-4">Paiement en cours…</h1>
          <p className="text-muted-foreground">Génération de la carte cadeau et envoi des emails.</p>
        </>
      )}

      {state.status === "paid" && info && (
        <div data-testid="gc-success-confirmed">
          <Gift className="w-16 h-16 text-primary mx-auto mb-6" />
          <h1 className="font-display text-5xl mb-4">Carte cadeau envoyée ! 🎁</h1>
          <p className="text-muted-foreground text-lg mb-8">
            Le destinataire vient de recevoir sa carte cadeau par email avec son PDF.
          </p>

          <div className="rounded-xl border-2 border-primary/40 bg-card p-6 mb-8 inline-flex flex-col gap-3 max-w-md" data-testid="gc-code-notice">
            <div className="text-xs uppercase tracking-[0.3em] text-primary">Code envoyé par email</div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Pour ta sécurité, le code de la carte cadeau n'est visible que dans l'email
              envoyé à ton adresse et à celle du destinataire. Vérifie tes spams si besoin.
            </p>
          </div>

          <div className="flex gap-3 justify-center flex-wrap">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12 px-8">
              <Link to="/evenements">Voir les événements</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-8">
              <Link to="/cartes-cadeaux">Offrir à nouveau</Link>
            </Button>
          </div>
        </div>
      )}

      {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
        <div data-testid="gc-success-error">
          <XCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
          <h1 className="font-display text-4xl mb-4">
            {state.status === "timeout" ? "Traitement en cours" : "Problème"}
          </h1>
          <p className="text-muted-foreground mb-8">{state.message}</p>
          <Button asChild variant="outline" className="rounded-full h-12 px-8">
            <Link to="/cartes-cadeaux">Réessayer</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
